import { type ReactNode, useCallback, useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { RiFileTextLine, RiSearchLine } from '@remixicon/react';

import {
  DialogCompositeMui as DialogComposite,
  useDebouncedSearch,
} from '@spotify/confidence-core-react';
import { CollaborationV1ExternalDocumentSourceType } from '@spotify/confidence-graphql';

import { useSearchProviderDocuments } from '../hooks/useSearchProviderDocuments';
import {
  SelectableProviderDocumentPage,
  hasSelectableProviderDocumentFields,
} from '../providerDocumentPage';

const useStyles = makeStyles(theme => ({
  listItemAvatar: {
    minWidth: 40,
  },
  avatar: {
    width: 28,
    height: 28,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
}));

const PROVIDER_NAMES: Record<
  CollaborationV1ExternalDocumentSourceType,
  string
> = {
  [CollaborationV1ExternalDocumentSourceType.GoogleDocs]: 'Google',
  [CollaborationV1ExternalDocumentSourceType.GoogleSlides]: 'Google',
  [CollaborationV1ExternalDocumentSourceType.Notion]: 'Notion',
};

type ProviderDocSearchDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (page: SelectableProviderDocumentPage) => void | Promise<void>;
  sourceType: CollaborationV1ExternalDocumentSourceType;
  title: string;
  searchLabel: string;
  isConnected: boolean;
  isCheckingConnection?: boolean;
  onConnect?: () => Promise<
    { linked: boolean; failureReason?: string } | undefined
  >;
  connectLabel?: string;
  onReconnect?: () => Promise<
    | {
        linked: boolean;
        failureReason?: string;
      }
    | undefined
  >;
  reconnectLabel?: string;
};

const ProviderDocSearchDialogContent = ({
  onClose,
  onSelect,
  sourceType,
  title,
  searchLabel,
  isConnected,
  isCheckingConnection = false,
  onConnect,
  connectLabel = 'Connect account',
  onReconnect,
  reconnectLabel = 'Update account access',
}: Omit<ProviderDocSearchDialogProps, 'isOpen'>) => {
  const classes = useStyles();
  const { search, query, setSearch } = useDebouncedSearch(300);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);

  const { pages, isLoading, error, refetch } = useSearchProviderDocuments({
    sourceType,
    query,
    pageSize: 10,
    skip: !isConnected || isCheckingConnection,
  });
  const selectablePages = pages.filter(hasSelectableProviderDocumentFields);

  const handleConnect = useCallback(async () => {
    if (!onConnect) return;

    setIsConnecting(true);
    setConnectError(null);
    try {
      const result = await onConnect();
      if (result && !result.linked) {
        setConnectError(result.failureReason ?? 'Failed to connect account');
      }
    } catch {
      setConnectError('Failed to connect account');
    } finally {
      setIsConnecting(false);
    }
  }, [onConnect]);

  const handleReconnect = useCallback(async () => {
    if (!onReconnect) return;

    setIsReconnecting(true);
    setReconnectError(null);
    try {
      const result = await onReconnect();
      if (result && !result.linked) {
        setReconnectError(
          result.failureReason ?? 'Failed to update account access',
        );
      } else {
        refetch();
      }
    } catch {
      setReconnectError('Failed to update account access');
    } finally {
      setIsReconnecting(false);
    }
  }, [onReconnect, refetch]);

  const handleSelect = useCallback(
    async (page: SelectableProviderDocumentPage) => {
      if (isSelecting) return;

      setIsSelecting(true);
      setSelectError(null);
      try {
        const selectResult = onSelect(page);
        if (selectResult) {
          await selectResult;
        }
        onClose();
      } catch {
        setSelectError('Failed to attach document');
      } finally {
        setIsSelecting(false);
      }
    },
    [isSelecting, onClose, onSelect],
  );

  const renderBodyContent = (): ReactNode => {
    if (isCheckingConnection) {
      return (
        <Typography variant="body1" color="textSecondary">
          Checking connection…
        </Typography>
      );
    }

    if (!isConnected) {
      const providerName = PROVIDER_NAMES[sourceType];
      return (
        <Box display="flex" flexDirection="column" gridGap={16}>
          <Typography variant="body1" color="textSecondary">
            You have not connected your {providerName} account yet. Connect to
            be able to attach your documents.
          </Typography>
          {connectError && (
            <Typography variant="body2" color="error">
              {connectError}
            </Typography>
          )}
          {onConnect && (
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                onClick={handleConnect}
                color="primary"
                disabled={isConnecting}
                startIcon={
                  isConnecting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
               data-track-source-anchor="4f093d24f2;provider-doc-search-dialog-content-button">
                {connectLabel}
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gridGap={12}>
        <TextField
          aria-label={searchLabel}
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <RiSearchLine size={16} />
              </InputAdornment>
            ),
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
         data-track-source-anchor="92fab5f327;provider-doc-search-dialog-content-text-field"/>
        {isLoading && (
          <Typography variant="body2" color="textSecondary">
            Searching…
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {onReconnect
              ? `Unable to access your pages. Use "${reconnectLabel}" below to reconnect.`
              : error}
          </Typography>
        )}
        {selectError && (
          <Typography variant="body2" color="error">
            {selectError}
          </Typography>
        )}
        {isSelecting && (
          <Typography variant="body2" color="textSecondary">
            Attaching…
          </Typography>
        )}
        {!isLoading && !error && selectablePages.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No pages found
          </Typography>
        )}
        {selectablePages.length > 0 && (
          <List aria-label="Search results">
            {selectablePages.map(page => (
              <ListItem
                key={page.id}
                button
                disableGutters
                onClick={() => {
                  void handleSelect(page);
                }}
               data-track-source-anchor="0477e23f30;provider-doc-search-dialog-content-list-item">
                <ListItemAvatar className={classes.listItemAvatar}>
                  <Avatar variant="rounded" className={classes.avatar}>
                    <RiFileTextLine size={16} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={page.title ?? 'Untitled'} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  return (
    <DialogComposite.Content>
      <DialogComposite.Header>
        <DialogComposite.Title>{title}</DialogComposite.Title>
        <DialogComposite.CloseButton  data-track-source-anchor="30777f1b61;provider-doc-search-dialog-content-close-button;low"/>
      </DialogComposite.Header>
      <DialogComposite.Body>{renderBodyContent()}</DialogComposite.Body>
      <DialogComposite.Actions>
        {isConnected && onReconnect && (
          <Box display="flex" alignItems="center" gridGap={8}>
            <Button
              variant="outlined"
              onClick={handleReconnect}
              disabled={isReconnecting}
              startIcon={
                isReconnecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
             data-track-source-anchor="e83f3ab000;provider-doc-search-dialog-content-button-2">
              {reconnectLabel}
            </Button>
            {reconnectError && (
              <Typography variant="body2" color="error">
                {reconnectError}
              </Typography>
            )}
          </Box>
        )}
        <DialogComposite.Actions.Cancel />
      </DialogComposite.Actions>
    </DialogComposite.Content>
  );
};

export const ProviderDocSearchDialog = ({
  isOpen,
  onClose,
  ...rest
}: ProviderDocSearchDialogProps) => {
  if (!isOpen) return null;

  return (
    <DialogComposite.Root
      open
      onClose={onClose}
      width="sm"
      PaperProps={{ style: { minWidth: 400 } }}
    >
      <ProviderDocSearchDialogContent onClose={onClose} {...rest} />
    </DialogComposite.Root>
  );
};
